import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Phone, Calendar, Users, MessageSquare, CheckCircle, Clock, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInquiries();
    fetchStats();
  }, [filter]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const filterParam = filter !== 'all' ? `?status_filter=${filter}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/inquiries${filterParam}`);
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/inquiries/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/inquiries/${inquiryId}?new_status=${newStatus}`);
      toast.success('Status updated successfully');
      fetchInquiries();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Clock },
      contacted: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: MessageSquare },
      completed: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-semibold border ${badge.color}`}>
        <Icon size={14} />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-burgundy-900 via-red-900 to-burgundy-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-orange-200 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Website</span>
          </button>
          <h1 className="text-4xl font-bold">Admin Dashboard</h1>
          <p className="text-orange-200 mt-2">Manage customer inquiries and event bookings</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold">Total Inquiries</p>
                <p className="text-3xl font-bold text-burgundy-900 mt-2">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                <Mail className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold">Pending</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">{stats.pending}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Clock className="text-orange-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold">Contacted</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">{stats.contacted}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <MessageSquare className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold">Completed</p>
                <p className="text-3xl font-bold text-emerald-600 mt-2">{stats.completed}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="text-emerald-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-lg border-2 border-orange-100 mb-6">
          <div className="flex space-x-3">
            {['all', 'pending', 'contacted', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  filter === status
                    ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="capitalize">{status}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Inquiries List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading inquiries...</p>
          </div>
        ) : inquiries.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-lg border-2 border-orange-100">
            <XCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 text-lg">No inquiries found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-100 hover:border-orange-300 transition-all"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-burgundy-900">{inquiry.name}</h3>
                    <p className="text-sm text-gray-500">Submitted {formatDate(inquiry.created_at)}</p>
                  </div>
                  {getStatusBadge(inquiry.status)}
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="text-orange-500" size={18} />
                    <a href={`mailto:${inquiry.email}`} className="text-gray-700 hover:text-orange-600">
                      {inquiry.email}
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="text-orange-500" size={18} />
                    <span className="text-gray-700">{inquiry.phone}</span>
                  </div>
                  {inquiry.event_date && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="text-orange-500" size={18} />
                      <span className="text-gray-700">{inquiry.event_date}</span>
                    </div>
                  )}
                  {inquiry.guest_count && (
                    <div className="flex items-center space-x-3">
                      <Users className="text-orange-500" size={18} />
                      <span className="text-gray-700">{inquiry.guest_count} guests</span>
                    </div>
                  )}
                </div>

                {inquiry.event_type && (
                  <div className="mb-4">
                    <span className="inline-block bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {inquiry.event_type}
                    </span>
                  </div>
                )}

                <div className="bg-orange-50 border-l-4 border-orange-500 rounded p-4 mb-4">
                  <p className="text-gray-700">{inquiry.message}</p>
                </div>

                {/* Status Update Buttons */}
                <div className="flex space-x-3">
                  <span className="text-sm text-gray-600 font-semibold mr-2">Update Status:</span>
                  {['pending', 'contacted', 'completed'].map((status) => (
                    inquiry.status !== status && (
                      <button
                        key={status}
                        onClick={() => updateStatus(inquiry.id, status)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gradient-to-r hover:from-orange-500 hover:to-yellow-500 hover:text-white rounded-lg text-sm font-semibold transition-all"
                      >
                        Mark as {status}
                      </button>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
